import React, { useEffect, useState } from "react";
import logger from "../lib/logger";
import { Eyebrow, Btn } from "../components/Primitives";
import { api } from "../lib/api";
import { toast } from "sonner";
import MarketStanding from "../components/MarketStanding";

export default function Referral({ user, onBackToPaywall, onUnlocked }) {
    const [count, setCount] = useState(0);

    const refLink = `${window.location.origin}/?ref=${user.referral_code}`;
    // Display a clean, brand-safe share link (decoupled from preview hostname)
    const displayLink = `youreverafter.com/r/${user.referral_code}`;

    useEffect(() => {
        let cancelled = false;
        const tick = async () => {
            try {
                const r = await api.referralCount(user.referral_code);
                if (cancelled) return;
                setCount(r.count);
                if (r.count >= 3) {
                    onUnlocked && onUnlocked();
                }
            } catch (err) {
                logger.warn("referral count poll failed:", err);
            }
        };
        tick();
        const id = setInterval(tick, 4000);
        return () => { cancelled = true; clearInterval(id); };
    }, [user.referral_code, onUnlocked]);

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(refLink);
            toast.success("Link copied");
        } catch {
            toast.error("Could not copy. Long-press to copy.");
        }
    };

    const shareiMessage = () => {
        const body = encodeURIComponent(`I took this archetype thing and it cracked me open. Try it: ${refLink}`);
        window.location.href = `sms:&body=${body}`;
    };

    const shareInstagram = async () => {
        // Instagram has no direct web share intent; copy and instruct
        try {
            await navigator.clipboard.writeText(refLink);
        } catch (err) {
            logger.warn("clipboard write failed:", err);
        }
        if (navigator.share) {
            try {
                await navigator.share({ title: "Ever After", text: "Discover your love archetype.", url: refLink });
                return;
            } catch (err) {
                logger.warn("native share canceled:", err);
            }
        }
        toast.success("Link copied — paste in your Instagram Story");
    };

    return (
        <div className="flex-1 flex flex-col bg-cream" data-testid="referral-screen">
            <div className="px-6 pt-6 pb-2 flex items-center justify-between">
                <button
                    data-testid="referral-back-btn"
                    onClick={onBackToPaywall}
                    className="tap-target text-[11px] tracking-[0.28em] uppercase text-ink-muted"
                >
                    ← Back
                </button>
                <span className="text-[10px] tracking-[0.32em] uppercase text-ink-muted">Referral</span>
            </div>

            <div className="flex-1 px-7 pt-8 pb-10 space-y-8">
                <div>
                    <Eyebrow>Unlock by invitation</Eyebrow>
                    <h1 className="mt-3 font-display text-4xl leading-[1.1] text-burgundy tracking-tight">
                        <span className="italic">Invite three</span> friends to unlock your full profile.
                    </h1>
                    <p className="mt-4 font-display italic text-lg text-ink-muted leading-snug">
                        Share your code. When 3 friends sign up, your profile unlocks automatically.
                    </p>
                </div>

                {/* Code display */}
                <div className="border border-burgundy/20 bg-cream-soft p-6 text-center">
                    <Eyebrow>Your code</Eyebrow>
                    <div
                        data-testid="referral-code"
                        className="mt-3 font-display text-5xl tracking-[0.18em] text-burgundy"
                    >
                        {user.referral_code}
                    </div>
                    <div className="mt-3 text-xs text-ink-muted break-all">
                        {displayLink}
                    </div>
                </div>

                {/* Progress */}
                <div>
                    <div className="flex justify-between items-baseline">
                        <Eyebrow>Progress</Eyebrow>
                        <span className="font-display text-2xl text-burgundy" data-testid="referral-count">
                            {count} <span className="text-ink-light">of 3</span>
                        </span>
                    </div>
                    <div className="mt-3 grid grid-cols-3 gap-2">
                        {[0, 1, 2].map(i => (
                            <div
                                key={i}
                                className={`h-2 transition-colors ${
                                    i < count ? "bg-gold" : "bg-burgundy/10"
                                }`}
                            />
                        ))}
                    </div>
                    <p className="mt-3 text-xs text-ink-muted">
                        {count >= 3 ? "Unlocked. Welcome in." : `${3 - count} more to unlock.`}
                    </p>
                </div>

                {/* Market standing */}
                <MarketStanding sessionId={user.session_id} />

                {/* Share buttons */}
                <div className="space-y-2.5">
                    <Btn data-testid="share-copy-btn" onClick={copy} variant="secondary">
                        Copy link
                    </Btn>
                    <Btn data-testid="share-imessage-btn" onClick={shareiMessage} variant="secondary">
                        Share to iMessage
                    </Btn>
                    <Btn data-testid="share-instagram-btn" onClick={shareInstagram} variant="secondary">
                        Share to Instagram Stories
                    </Btn>
                </div>
            </div>

            <div className="px-6 pb-8 text-center">
                <button
                    data-testid="referral-instant-unlock"
                    onClick={onBackToPaywall}
                    className="tap-link text-burgundy text-xs tracking-[0.28em] uppercase underline-offset-4 hover:underline"
                >
                    Or unlock instantly →
                </button>
            </div>
        </div>
    );
}
